import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { format } from "date-fns/format";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import SpinnerCentered from "~/components/Loader/SpinnerCentered";
import GoogleMap from "google-map-react";
import MapPin from "~/components/MapPin";

import OpenTaxiLogo from "~/assets/logo.png";
import { getVehicles } from "~/services/http/vehicles";
import { httpGetOrdersAnalytics } from "~/services/http/orders";
import MyToggleButton from "components/Inputs/MyToggleButton";

import useStyles from "./Map.css";

const Map = (props) => {
  const { updateIndex, myCompany, myUser } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [filters, setFilters] = useState({
    active: true,
  });
  const [counts, setCounts] = useState({
    all: "",
    active: "",
  });
  const [updated, setUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState();

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setFilters({
      ...filters,
      ...updated_values,
    });
  };

  const handleFormChange = ({ target }) => {
    updateValues({ [target.name]: target.value });
  };

  // Maps
  const [mapView, setMapView] = useState({ center: null, zoom: null });
  const [mapMarkers, setMapMarkers] = useState([
    /*
    <MapPin
        lat={65.741948} // Kemi
        lng={24.567019}
        key="1"
        size={40}
        weight={3}
        title={null}
      />,
    */
  ]);
  const mapViewRef = useRef(null);

  const createMapOptions = (maps) => {
    return {
      mapTypeControl: false,
      mapTypeControlOptions: {
        position: maps.ControlPosition.TOP_RIGHT,
      },
      fullscreenControl: false,
      maxZoom: 11,
      minZoom: 5,
    };
  };

  const initMap = () => {
    // Finland
    const bounds = {
      nw: { lat: 70.302004, lng: 15.588492 },
      se: { lat: 60.748082, lng: 31.750109 },
    };

    setMapView(bounds);
  };

  const handleMapPinClick = (key, _childProps) => {
    console.log("handleMapPinClick", key);
  };

  const handleMapChange = () => {
    // Map view updates intentionally not tracked in component state
  };

  const handleGetOrdersAnalytics = useCallback(() => {
    httpGetOrdersAnalytics().then((response) => {
      setAnalytics(response);
    });
  }, []);

  const handleGetVehicles = useCallback(() => {
    let params = {
      ...filters,
    };

    if (myUser && myUser.is_staff === false) {
      params.company__id = myCompany?.id;
    }

    let request = {
      params: params,
    };

    setLoading(true);
    getVehicles(request).then(
      (response) => {
        if (filters?.active === true) {
          setCounts((prev) => ({
            ...prev,
            active: response.length,
          }));
        } else {
          setCounts((prev) => ({
            ...prev,
            all: response.length,
          }));
        }

        setUpdated(new Date());

        let vehicles = [];
        response.map((vehicle) => {
          if (vehicle?.coordinates) {
            vehicles.push(
              <MapPin
                key={vehicle?.id}
                lat={vehicle?.coordinates?.coordinates[0]}
                lng={vehicle?.coordinates?.coordinates[1]}
                size={20}
                weight={3}
                title={vehicle?.license_number + " / " + vehicle?.company?.name}
                backgroundColor={
                  vehicle?.color === "#FFFFFF" ? "#6A31DC" : "#FFF"
                }
                color={vehicle?.color}
                borderColor={vehicle?.color}
                opacity={1}
                pinContent={
                  vehicle?.license_number ? vehicle?.license_number : ""
                }
              />,
            );
          }
        });

        setMapMarkers(vehicles);
        setLoading(false);
      },
      (_errors) => {
        setLoading(false);
      },
    );
  }, [filters, myCompany?.id, myUser]);

  useEffect(() => {
    initMap();
    handleGetOrdersAnalytics();
  }, [handleGetOrdersAnalytics]);

  useEffect(() => {
    handleGetVehicles();
  }, [filters, updateIndex, handleGetVehicles]);

  useEffect(() => {
    const interval = setInterval(() => {
      handleGetVehicles();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [filters, handleGetVehicles]);

  const formatDateTime = (dt) => {
    if (!dt) return "";
    const d = dt instanceof Date ? dt : new Date(dt);
    return Number.isNaN(d.getTime()) ? "" : format(d, "dd.MM.yyyy HH:mm:ss");
  };

  return (
    <Fragment>
      <div style={{ position: "relative" }}>
        <div className={classes.topLeftBoxFixed}>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Grid size={12}>
              <img src={OpenTaxiLogo} style={{ width: "256px" }} />
              {/*<Typography variant="subtitle2">OpenTaxi</Typography>*/}
            </Grid>
            <Grid size={12}>
              {/* {key:0, label: t("Kaikki"), value: null},  */}
              <MyToggleButton
                name="active"
                value={filters.active}
                onChange={handleFormChange}
                buttons={[
                  {
                    key: 0,
                    label: myUser?.is_staff ? t("Kaikki") : t("Kaikki omat"),
                    value: null,
                  },
                  {
                    key: 1,
                    label: `${myUser?.is_staff ? t("Aktiiviset autot") : t("Omat aktiiviset autot")} (${counts?.active})`,
                    value: true,
                  },
                ]}
              />
              <div>
                <Typography>{formatDateTime(updated)}</Typography>
                <Typography>
                  {t("Suoritettuja tilauksia")}: {analytics?.orders_complete}
                </Typography>
                {loading && <SpinnerCentered />}
              </div>
            </Grid>
          </Grid>
        </div>

        <div ref={mapViewRef} style={{ width: "100%", height: "100vh" }}>
          <GoogleMap
            options={createMapOptions}
            bootstrapURLKeys={{
              key: "AIzaSyA2_4q2PAhogZ0qqRFvsNHQlOrZe_fGAf4",
              v: "3.31",
            }}
            defaultCenter={{ lat: 65.162, lng: 23.947 }}
            center={mapView.center}
            defaultZoom={5}
            zoom={mapView.zoom}
            onChildClick={handleMapPinClick}
            /*
            onChange={({ zoom, bounds }) => {
              setZoom(zoom);
              setBounds([
                bounds.nw.lng,
                bounds.se.lat,
                bounds.se.lng,
                bounds.nw.lat
              ]);
            }}
            */
            onChange={handleMapChange}
          >
            {mapMarkers}
          </GoogleMap>
        </div>
      </div>
    </Fragment>
  );
};

export default Map;
